import { useState } from 'react'
import { useClinicsByAdvertisementId } from '@/hooks/useClinicsByAdvertisementId'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Building, MapPin, Phone, Download } from 'lucide-react'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

interface AdvertisementClinicsProps {
  advertisementId?: number
  onOpenChange: (open: boolean) => void
}

export function AdvertisementClinics({ advertisementId }: AdvertisementClinicsProps) {
  const { clinics, isLoading, isError } = useClinicsByAdvertisementId({
    advertisement_id: advertisementId || 0,
  })
  const [exportOpen, setExportOpen] = useState(false)
  const fieldOptions = [
    { key: 'clinic_name', label: '診所名稱', default: true },
    { key: 'area', label: '地區', default: false },
    { key: 'address', label: '地址', default: false },
    { key: 'phone', label: '電話', default: false },
  ] as const
  const [selectedFields, setSelectedFields] = useState<Set<string>>(
    new Set(fieldOptions.filter((f) => f.default).map((f) => f.key)),
  )

  const toggleField = (key: string, checked: boolean | string) => {
    setSelectedFields((prev) => {
      const next = new Set(prev)
      if (checked) next.add(key)
      else next.delete(key)
      return next
    })
  }

  const handleExport = () => {
    if (!clinics || clinics.length === 0 || selectedFields.size === 0) {
      setExportOpen(false)
      return
    }

    // Build data rows based on selected fields
    const headers = fieldOptions.filter((f) => selectedFields.has(f.key)).map((f) => f.label)
    const rows = clinics.map((c) =>
      fieldOptions
        .filter((f) => selectedFields.has(f.key))
        .map((f) => {
          const value = c[f.key]
          return value == null ? '' : value
        }),
    )

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
    XLSX.utils.book_append_sheet(wb, ws, '診所清單')

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const data = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const today = new Date()
    const yyyy = today.getFullYear()
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const dd = String(today.getDate()).padStart(2, '0')
    const idPart = advertisementId ? `_${advertisementId}` : ''
    saveAs(data, `廣告診所清單${idPart}_${yyyy}${mm}${dd}.xlsx`)
    setExportOpen(false)
  }

  if (isLoading) {
    return <div className="flex items-center justify-center p-6">載入中...</div>
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center p-6 text-destructive">
        載入失敗，請重新整理頁面
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6 flex flex-col h-full">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">已排程的診所</h3>
          <p className="text-sm text-muted-foreground">此廣告已排程在以下診所播放。</p>
        </div>
        {clinics.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => setExportOpen(true)}>
            <Download className="h-4 w-4 mr-2" /> 匯出清單
          </Button>
        )}
      </div>

      {clinics.length === 0 ? (
        <div className="p-4 text-center text-muted-foreground border rounded-md">
          此廣告尚未排程在任何診所播放
        </div>
      ) : (
        <div className="overflow-y-auto pr-2 flex-1">
          <div className="grid grid-cols-1 gap-4">
            {clinics?.map((clinic) => (
              <Card key={clinic.clinic_id} className="overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex flex-col space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Building className="h-5 w-5 text-muted-foreground" />
                        <h4 className="font-medium">{clinic.clinic_name}</h4>
                      </div>
                      <Badge variant="outline" className="ml-2">
                        {clinic.is_active ? '啟用中' : '已停用'}
                      </Badge>
                    </div>

                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span>{clinic.address || '無地址資料'}</span>
                    </div>

                    {clinic.phone && (
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        <span>{clinic.phone}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>選擇匯出欄位</DialogTitle>
            <DialogDescription>選擇要輸出的欄位後，點擊「輸出 Excel」。</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {fieldOptions.map((f) => (
              <div key={f.key} className="flex items-center space-x-2">
                <Checkbox
                  id={`field-${f.key}`}
                  checked={selectedFields.has(f.key)}
                  onCheckedChange={(checked) => toggleField(f.key, checked)}
                />
                <Label htmlFor={`field-${f.key}`}>{f.label}</Label>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOpen(false)}>
              取消
            </Button>
            <Button onClick={handleExport}>輸出 Excel (.xlsx)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
